'use client';

import { memo, useMemo } from 'react';
import { Box } from '@mui/material';
import ReactMarkdown, { Components } from 'react-markdown';
import styled from '@emotion/styled';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { BodyMedium, TitleLarge, TitleMedium, TitleSmall } from '@dh/styles';

export type FormattedOutputProps = {
    children: string | null | undefined;
    maxLength?: number;
    replaceNewLines?: boolean;
    color?: string;
    clampLines?: number;
};

// Styled components for markdown elements (defined outside component for performance)
const StyledParagraph = styled(BodyMedium)`
    margin: 0 0 12px 0;
    padding: 0;

    &:last-child {
        margin-bottom: 0;
    }
`;

const StyledH1 = styled(TitleLarge)`
    margin: 16px 0 8px 0;

    &:first-of-type {
        margin-top: 0;
    }
`;

const StyledH2 = styled(TitleMedium)`
    margin: 14px 0 6px 0;

    &:first-of-type {
        margin-top: 0;
    }
`;

const StyledH3 = styled(TitleSmall)`
    margin: 12px 0 4px 0;

    &:first-of-type {
        margin-top: 0;
    }
`;

const StyledUl = styled('ul')`
    margin: 8px 0;
    padding-left: 24px;

    li {
        font-family: ${({ theme }) => theme.fonts.primary};
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        letter-spacing: 0.25px;
        margin: 4px 0;
    }
`;

const StyledOl = styled('ol')`
    margin: 8px 0;
    padding-left: 24px;

    li {
        font-family: ${({ theme }) => theme.fonts.primary};
        font-size: 16px;
        font-weight: 500;
        line-height: 22px;
        letter-spacing: 0.25px;
        margin: 4px 0;
    }
`;

const StyledStrong = styled('strong')`
    font-weight: 700;
`;

const StyledEm = styled('em')`
    font-style: italic;
`;

const StyledLink = styled('a')`
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
        text-decoration: none;
    }

    &:visited {
        color: ${({ theme }) => theme.colors.primary};
    }
`;

const ExternalLinkIcon = styled(OpenInNewIcon)`
    font-size: 14px;
    vertical-align: text-top;
    transform: translate(-2px, -6px);
`;

const ClampedContainer = styled(Box) <{ lines: number }>`
    display: -webkit-box;
    -webkit-line-clamp: ${({ lines }) => lines};
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const FormattedOutputComponent = ({
    children,
    maxLength,
    replaceNewLines = true,
    color,
    clampLines
}: FormattedOutputProps) => {
    // Memoize text processing to avoid unnecessary recalculations
    const processedText = useMemo(() => {
        if (children === null || children === undefined) {
            return null;
        }

        let text = children;

        if (maxLength && text.length > maxLength) {
            text = text.slice(0, maxLength) + ' \n ...';
        }

        if (replaceNewLines) {
            text = text.replaceAll('\n', '  \n ');
        }

        return text;
    }, [children, maxLength, replaceNewLines]);

    // Memoize components object to prevent recreation on every render
    const components: Components = useMemo(
        () => ({
            p: ({ children }) => <StyledParagraph color={color}>{children}</StyledParagraph>,
            h1: ({ children }) => <StyledH1 color={color}>{children}</StyledH1>,
            h2: ({ children }) => <StyledH2 color={color}>{children}</StyledH2>,
            h3: ({ children }) => <StyledH3 color={color}>{children}</StyledH3>,
            ul: ({ children }) => <StyledUl>{children}</StyledUl>,
            ol: ({ children }) => <StyledOl>{children}</StyledOl>,
            strong: ({ children }) => <StyledStrong>{children}</StyledStrong>,
            em: ({ children }) => <StyledEm>{children}</StyledEm>,
            a: ({ children, href }) => {
                const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
                return (
                    <StyledLink
                        href={href}
                        target={isExternal ? '_blank' : undefined}
                        rel={isExternal ? 'noopener noreferrer' : undefined}
                    >
                        {children}
                        {isExternal && <ExternalLinkIcon />}
                    </StyledLink>
                );
            },
            img: ({ alt, src }) => <img alt={alt} src={src} style={{ maxWidth: '100%' }} />
        }),
        [color]
    );

    if (processedText === null) {
        return null;
    }

    const content = (
        <Box color={color}>
            <ReactMarkdown components={components}>{processedText}</ReactMarkdown>
        </Box>
    );

    if (clampLines) {
        return <ClampedContainer lines={clampLines}>{content}</ClampedContainer>;
    }

    return content;
};

// Export memoized component to prevent unnecessary re-renders
export const FormattedOutput = memo(FormattedOutputComponent);
